import * as breakpoint from '@pixelunion/breakpoint';
import EventHandler from '@pixelunion/events';
import ImageZoom from './ImageZoom';
import loadYoutubeIframeAPI from '../utils/youtubeIframeAPIHelper';
import Vimeo from '@vimeo/player'

export default class ProductGallery {
  constructor(el, settings) {
    this.el = el;
    this.settings = settings;
    this.layout = this.el.dataset.productGalleryLayout;
    this.viewport = this.el.querySelector('[data-product-gallery-viewport]');
    this.navigation = this.el.querySelector('[data-product-gallery-navigation]');
    this.figures = this.viewport.querySelectorAll('[data-product-gallery-figure]');
    this.media = [];
    this.selected = {
      figure: this.viewport.querySelector('[data-product-gallery-selected="true"]'),
      thumbnail: this.navigation ? this.navigation.querySelector('[data-product-gallery-selected="true"]') : null,
    };

    this.events = new EventHandler();

    if (this.navigation) {
      this.thumbnails = this.navigation.querySelectorAll('[data-product-gallery-thumbnail]');
      this.onThumbnailClick = e => (
        this._selectMediaByIndex(e.currentTarget.dataset.productGalleryThumbnail)
      );

      this.thumbnails.forEach(thumbnail => this.events.register(thumbnail, 'click', this.onThumbnailClick));
    }

    const photoswipeJsonEl = this.el.querySelector('[data-photoswipe-json]');

    if (photoswipeJsonEl) {
      const photoswipeJson = JSON.parse(photoswipeJsonEl.innerHTML);

      this.imageZoom = new ImageZoom(photoswipeJson.images);
      this.zoomableImages = this.viewport.querySelectorAll('[data-image-zoom]');
      this.onImageZoomClick = e => this.imageZoom.open(e.currentTarget);

      this.zoomableImages.forEach(image => this.events.register(image, 'click', this.onImageZoomClick));
    }

    const features = [];

    if (this.el.querySelector('[data-media-type="model"]')) {
      this.events.register(this.viewport, 'click', e => {
        if ('shopifyXr' in e.target.dataset) {
          this._onViewInYourSpaceClick(e.target);
        }
      });

      features.push(
        {
          name: 'model-viewer-ui',
          version: '1.0',
          onLoad: this._onModelLibraryLoad.bind(this),
        },
      );
      features.push(
        {
          name: 'shopify-xr',
          version: '1.0',
        },
      );
    }

    if (this.el.querySelector('[data-media-type="video"]')) {
      Promise.all([
        new Promise(resolve => {
          if (!document.querySelector('#plyr-stylesheet')) {
            const link = document.createElement('link');

            link.setAttribute('id', 'plyr-stylesheet');
            link.setAttribute('rel', 'stylesheet');
            link.setAttribute('href', 'https://cdn.shopify.com/shopifycloud/shopify-plyr/v1.0/shopify-plyr.css');
            link.onload = resolve;
            document.body.appendChild(link);
          } else {
            resolve();
          }
        }),
        new Promise(resolve => {
          features.push({
            name: 'video-ui',
            version: '1.0',
            onLoad: resolve,
          });
        }),
      ]).then(this._onVideoLibraryLoad.bind(this));
    }

    loadYoutubeIframeAPI().then(this._onExternalVideoLibraryLoad.bind(this));

    if (features.length) {
      Shopify.loadFeatures(features);
    }
  }

  selectMediaByVariant(variant) {
    if (!variant.featured_media) return;

    const figure = this.viewport.querySelector(`[data-media="${variant.featured_media.id}"]`);
    this._selectMediaByIndex(figure.dataset.productGalleryFigure);
  }

  unload() {
    this.events.unregisterAll();

    this.media.forEach(m => m.unload());

    if (this.imageZoom) {
      this.imageZoom.unload();
    }
  }

  _onModelLibraryLoad() {
    const modelFigures = this.viewport.querySelectorAll('[data-media-type="model"]');
    const controls = ['zoom-in', 'zoom-out'];
    if (document.fullscreenEnabled) controls.push('fullscreen');

    modelFigures.forEach(figure => {
      const player = new Shopify.ModelViewerUI(figure.querySelector('model-viewer'), { controls });
      this.media.push({
        type: 'model',
        figure,
        player,
        focus: () => {},
        restart: () => {},
        pause: () => player.pause(),
        play: () => player.play(),
        unload: () => {},
      });
    });
  }

  _onViewInYourSpaceClick(target) {
    if (target.dataset.shopifyModel3dId === this.selected.figure.dataset.media) return;

    const figure = this.viewport.querySelector(`[data-media="${target.dataset.shopifyModel3dId}"]`);
    this._selectMediaByEl(figure);
  }

  _selectMediaByEl(el) {
    this._selectMediaByIndex(parseInt(el.dataset.productGalleryFigure, 10));
  }

  _onVideoLibraryLoad() {
    const videoFigures = this.viewport.querySelectorAll('[data-media-type="video"]');

    for (let i = 0; i < videoFigures.length; i++) {
      const videoEl = videoFigures[i];
      const player = new Shopify.Plyr(
        videoEl.querySelector('video'),
        {
          loop: { active: this.settings.enable_video_looping },
        },
      );
      const plyrEl = videoEl.querySelector('.plyr');
      const video = {
        type: 'video',
        figure: videoEl,
        player,
        focus: () => plyrEl.focus(),
        restart: () => {
          player.restart();
          player.play();
        },
        pause: () => player.pause(),
        play: () => player.play(),
        unload: () => player.destroy(),
      };

      this.events.register(videoEl, 'play', () => this._pauseMedia(video));
      this.media.push(video);
    }
  }

  _onExternalVideoLibraryLoad() {
    const videoFigures = this.viewport.querySelectorAll('[data-media-type="external_video"]');

    videoFigures.forEach(videoFigure => {
      const videoIframe = videoFigure.querySelector('iframe');
      const iframeSrc = videoIframe.src;
      let video;

      if (iframeSrc.includes('youtube')) {
        const player = new YT.Player(videoIframe);
        video = {
          type: 'video',
          figure: videoFigure,
          player,
          focus: () => player.getIframe().focus(),
          restart: () => {
            player.seekTo(0);
            player.playVideo();
          },
          pause: () => player.pauseVideo(),
          play: () => player.playVideo(),
          unload: () => {},
        };

        this.events.register(player, 'onStateChange', ({ data }) => {
          if (data === YT.PlayerState.ENDED && this.settings.enable_video_looping) {
            video.restart();
          } else if (data === YT.PlayerState.PLAYING) {
            this._pauseMedia(video);
          }
        });
      } else {
        const player = new Vimeo(videoIframe);
        player.setLoop(this.settings.enable_video_looping);

        video = {
          type: 'video',
          figure: videoFigure,
          player,
          focus: () => videoIframe.focus(),
          restart: () => {
            player.setCurrentTime(0);
            player.play();
          },
          pause: () => player.pause(),
          play: () => player.play(),
          unload: () => player.unload(),
        }
      }
      this.media.push(video);
    });
  }

  _onMediaSelect(media) {
    if (!media) return;

    switch (media.type) {
      case 'video':
        media.focus();
        if (this.settings.enable_video_autoplay && breakpoint.min('L')) {
          media.play();
        }
        break;
      case 'model':
        if (document.querySelector('html').classList.contains('no-touch')) {
          media.play();
        }
        break;
      default:
        break;
    }
  }

  _pauseMedia(excludeMedia = null) {
    this.media.forEach(m => {
      if (m !== excludeMedia) {
        m.pause();
      }
    });
  }

  _selectMediaByIndex(index) {
    const figure = this.figures[index];

    this.selected.figure.dataset.productGallerySelected = false;
    this.selected.figure.setAttribute('aria-hidden', true);

    this.selected.figure = figure;
    this.selected.figure.dataset.productGallerySelected = true;
    this.selected.figure.setAttribute('aria-hidden', false);

    if (this.navigation) {
      const thumbnail = this.thumbnails[index];

      this.selected.thumbnail.dataset.productGallerySelected = false;
      this.selected.thumbnail = thumbnail;
      this.selected.thumbnail.dataset.productGallerySelected = true;
    }

    this._pauseMedia();
    this._onMediaSelect(this.media.filter(m => m.figure === this.selected.figure)[0]);
  }
}
